import { signOut } from "aws-amplify/auth";
import {
  BarChart3,
  Camera,
  Dumbbell,
  ListChecks,
  LogOut,
  Settings2,
} from "lucide-react";
import { lazy, Suspense } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ThemaSchalter } from "@/ThemaSchalter";

/**
 * Ansichten getrennt nachladen.
 *
 * Die Diagrammbibliothek macht den Großteil des Bündels aus, und wer nur ein
 * Foto hochlädt, braucht sie nicht. Am Handy im Mobilfunknetz ist das der
 * Unterschied zwischen „geht sofort“ und „lädt“.
 */
const Verlauf = lazy(() =>
  import("./views/Verlauf").then((m) => ({ default: m.Verlauf })),
);
const Training = lazy(() =>
  import("./views/Training").then((m) => ({ default: m.Training })),
);
const Zuordnen = lazy(() =>
  import("./views/Zuordnen").then((m) => ({ default: m.Zuordnen })),
);
const Geraete = lazy(() =>
  import("./views/Geraete").then((m) => ({ default: m.Geraete })),
);
const Foto = lazy(() => import("./views/Foto").then((m) => ({ default: m.Foto })));

const NAVIGATION = [
  { pfad: "/verlauf", text: "Verlauf", Icon: BarChart3 },
  { pfad: "/training", text: "Training", Icon: Dumbbell },
  { pfad: "/foto", text: "Foto", Icon: Camera },
  { pfad: "/zuordnen", text: "Zuordnen", Icon: ListChecks },
  { pfad: "/geraete", text: "Geräte", Icon: Settings2 },
];

export function App({ onAbgemeldet }: { onAbgemeldet: () => void }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center gap-2 px-4">
          <Dumbbell className="size-5 text-primary" />
          <span className="font-semibold tracking-tight">GymBook</span>
          <div className="flex-1" />
          <ThemaSchalter />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Abmelden"
            onClick={async () => {
              await signOut();
              onAbgemeldet();
            }}
          >
            <LogOut />
          </Button>
        </div>

        {/* Auf breiten Anzeigen unter dem Titel, am Handy unten am Rand —
            dort ist der Daumen. */}
        <nav className="hidden border-t sm:block">
          <div className="mx-auto flex w-full max-w-5xl gap-1 px-2">
            {NAVIGATION.map(({ pfad, text, Icon }) => (
              <NavLink key={pfad} to={pfad} className="flex-1">
                {({ isActive }) => (
                  <span
                    className={cn(
                      "flex items-center justify-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
                      isActive
                        ? "border-primary text-foreground"
                        : "border-transparent text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Icon className="size-4" />
                    {text}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 pb-24 sm:pb-6">
        <Suspense fallback={<LadeGeruest />}>
          <Routes>
            {/* Der Verlauf ist der Einstieg: erst der Stand, dann die Arbeit. */}
            <Route path="/" element={<Navigate to="/verlauf" replace />} />
            <Route path="/verlauf" element={<Verlauf />} />
            <Route path="/training" element={<Training />} />
            <Route path="/foto" element={<Foto />} />
            <Route path="/zuordnen" element={<Zuordnen />} />
            <Route path="/geraete" element={<Geraete />} />
            <Route path="*" element={<Navigate to="/verlauf" replace />} />
          </Routes>
        </Suspense>
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-10 border-t bg-background pb-[env(safe-area-inset-bottom)] sm:hidden">
        <div className="flex">
          {NAVIGATION.map(({ pfad, text, Icon }) => (
            <NavLink key={pfad} to={pfad} className="flex-1">
              {({ isActive }) => (
                <span
                  className={cn(
                    "flex flex-col items-center gap-1 py-2 text-[11px] font-medium transition-colors",
                    isActive ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  <Icon className="size-5" />
                  {text}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

function LadeGeruest() {
  return (
    <div className="grid gap-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
